'use client';

import { useEffect, useState } from 'react';
import { useAppStore } from '@/stores/app-store';
import { isDarkTheme } from '@/lib/helpers';
import { DashWidget, Plant, Device } from '@/lib/types';
import ApexChartWrapper from '../ApexChartWrapper';
import { NO_VALUE } from '@/lib/device-status';
import { ScadaShell, ScadaBelowContext, SideHead, MachineRow, K, mix, type Health } from './scada-panel';
import { ChillerPlantScadaView } from './chiller-plant-scada-view';
import { TrendCard } from './trend-card';

interface Props { widget: DashWidget; plant: Plant; }

interface FlowSeries { name: string; color: string; data: [number, number][] }

/*
 * Chiller Plant SCADA — a full-width live schematic of an air-cooled central
 * chilled-water plant (N chillers + N CHW pumps → building/AHU load → return).
 * Reads a single showcase device (config_data.staticDevices) by widget.deviceSN,
 * so the demo runs on fixed representative values with no telemetry tables.
 *
 * The schematic is data-driven: it renders one chiller per chN_* group present
 * (N = 1..4) and one pump per chwpN_* group, so a 1- or 2-chiller plant both
 * lay out cleanly. Any missing key degrades to "–" / is derived where possible.
 *
 * Expected device.data keys (all optional):
 *   plant_kw, cooling_load, kw_per_rt (=plant_kw/cooling_load), cop (=3.517/kw_per_rt),
 *   chw_supply, chw_return, chw_dt (=return-supply), chw_flow, setpoint, oat, daily,
 *   chN_status(1/0) chN_kw chN_load(%) chN_rt chN_kwrt,  chwpN_status(1/0) chwpN_kw
 */
export default function ChillerScadaWidget({ widget, plant }: Props) {
  const th = useAppStore((s) => s.lang) === 'th';

  const dev: Device | undefined =
    plant.devices.find((d) => d.sn === widget.deviceSN) || plant.devices[0];
  const d = (dev?.data || {}) as Record<string, number | undefined>;
  const online = dev ? dev.st === 'online' : false;

  // Optional live overrides: CHW supply/return flow can come from real flow-meter
  // devices (widget.supplySN / returnSN → their `flow_rate`), while the rest of
  // the SCADA stays demo (widget.demo shows a "DEMO DATA" badge to make that clear).
  const cfg = widget as unknown as { supplySN?: string; returnSN?: string; tempSN?: string; powerSN?: string; demo?: boolean; hideKpis?: boolean; loadName?: string; style?: 'illustrated'; trendEff?: number[]; trendEnergy?: number[] };
  const readField = (sn: string | undefined, key: string): number | null => {
    if (!sn) return null;
    const v = plant.devices.find((x) => x.sn === sn)?.data?.[key];
    return typeof v === 'number' && !isNaN(v) ? v : null;
  };
  const readFlow = (sn?: string) => readField(sn, 'flow_rate');

  const num = (k: string): number | null => (typeof d[k] === 'number' && !isNaN(d[k] as number) ? (d[k] as number) : null);
  const fmt = (v: number | null, dp = 1) => (v == null ? NO_VALUE : v.toFixed(dp));
  // A configured field — present even while the device is offline and its live
  // value is withheld, so the layout (how many chillers/pumps) stays put.
  const has = (k: string) => num(k) != null || !!dev?.fieldNames?.[k];

  // Real chiller power (widget.powerSN → `total_power`) overrides Plant Power +
  // the CH-1 kW label when a power meter is bound.
  const realPower = readField(cfg.powerSN, 'total_power');
  const i1 = readField(cfg.powerSN, 'current_1'), i2 = readField(cfg.powerSN, 'current_2'), i3 = readField(cfg.powerSN, 'current_3');
  const livePower = !!cfg.powerSN;
  const plantKw = realPower ?? num('plant_kw');
  const load = num('cooling_load');
  const kwRt = num('kw_per_rt') ?? (plantKw != null && load ? plantKw / load : null);
  const cop = num('cop') ?? (kwRt ? 3.517 / kwRt : null);
  // CHW supply temperature: real (widget.tempSN → `temp`) when bound, else demo.
  const liveTemp = !!cfg.tempSN;
  const chwS = readField(cfg.tempSN, 'temp') ?? num('chw_supply');
  const chwR = num('chw_return');
  const dt = num('chw_dt') ?? (chwS != null && chwR != null ? chwR - chwS : null);
  const flow = num('chw_flow');
  // Real supply/return flow (falls back to the static chw_flow when unbound).
  const supplyFlow = readFlow(cfg.supplySN) ?? flow;
  const returnFlow = readFlow(cfg.returnSN) ?? flow;
  const liveFlow = !!(cfg.supplySN || cfg.returnSN);
  // Live flow keeps the meter's real unit (e.g. l/m) + more precision; the static
  // demo flow stays m³/h · 0dp.
  const flowUnit = liveFlow
    ? (plant.devices.find((x) => x.sn === (cfg.supplySN || cfg.returnSN))?.units?.flow_rate || 'm³/h')
    : 'm³/h';
  const fmtFlow = (v: number | null) => (v == null ? NO_VALUE : liveFlow ? v.toFixed(v < 10 ? 2 : 1) : v.toFixed(0));

  // Live Supply-vs-Return flow comparison time-series (replaces the demo trends
  // at the bottom when real flow meters are bound).
  const [flowSeries, setFlowSeries] = useState<FlowSeries[]>([]);
  const flowKey = `${cfg.supplySN || ''}|${cfg.returnSN || ''}`;
  useEffect(() => {
    if (!liveFlow) return;
    let cancelled = false;
    const metersL = [
      { sn: cfg.supplySN, name: th ? 'อัตราการไหล · จ่าย' : 'Flow · Supply', color: 'var(--cyan)' },
      { sn: cfg.returnSN, name: th ? 'อัตราการไหล · กลับ' : 'Flow · Return', color: 'var(--amber)' },
    ].filter((m): m is { sn: string; name: string; color: string } => !!m.sn);
    Promise.all(metersL.map((m) =>
      fetch(`/api/devices/${m.sn}/data?keys=flow_rate&range=1d`).then((r) => (r.ok ? r.json() : null)).catch(() => null)
    )).then((results: Array<{ timestamps?: number[]; series?: { key: string; data: (number | null)[] }[] } | null>) => {
      if (cancelled) return;
      const out: FlowSeries[] = [];
      results.forEach((res, i) => {
        if (!res?.timestamps) return;
        const ser = (res.series || []).find((x) => x.key === 'flow_rate') || res.series?.[0];
        if (!ser) return;
        const pts: [number, number][] = [];
        for (let j = 0; j < res.timestamps.length; j++) { const v = ser.data[j]; if (v != null) pts.push([res.timestamps![j], v]); }
        if (pts.length) out.push({ name: metersL[i].name, color: metersL[i].color, data: pts });
      });
      setFlowSeries(out);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowKey, liveFlow, th]);

  // Live CHW supply temperature time-series (widget.tempSN → `temp`). The return
  // line stays demo (no return-temp sensor), aligned to the same timestamps.
  const [tempChart, setTempChart] = useState<FlowSeries[]>([]);
  useEffect(() => {
    if (!cfg.tempSN) return;
    let cancelled = false;
    fetch(`/api/devices/${cfg.tempSN}/data?keys=temp&range=1d`).then((r) => (r.ok ? r.json() : null)).catch(() => null)
      .then((res: { timestamps?: number[]; series?: { key: string; data: (number | null)[] }[] } | null) => {
        if (cancelled || !res?.timestamps) return;
        const ser = (res.series || []).find((x) => x.key === 'temp') || res.series?.[0];
        if (!ser) return;
        const sup: [number, number][] = [], ret: [number, number][] = [];
        const baseR = chwR ?? 12.8;
        for (let j = 0; j < res.timestamps.length; j++) {
          const v = ser.data[j];
          if (v != null) { sup.push([res.timestamps[j], v]); ret.push([res.timestamps[j], +(baseR + Math.sin(j / 5) * 0.4).toFixed(1)]); }
        }
        if (sup.length) setTempChart([
          { name: th ? 'อุณหภูมิ · จ่าย' : 'Temperature · Supply', color: 'var(--cyan)', data: sup },
          { name: th ? 'อุณหภูมิ · กลับ' : 'Temperature · Return', color: 'var(--amber)', data: ret },
        ]);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.tempSN, chwR, th]);

  // Live 3-phase current time-series (widget.powerSN → current_1/2/3).
  const [phaseSeries, setPhaseSeries] = useState<FlowSeries[]>([]);
  useEffect(() => {
    if (!cfg.powerSN) return;
    let cancelled = false;
    fetch(`/api/devices/${cfg.powerSN}/data?keys=current_1,current_2,current_3&range=1d`).then((r) => (r.ok ? r.json() : null)).catch(() => null)
      .then((res: { timestamps?: number[]; series?: { key: string; data: (number | null)[] }[] } | null) => {
        if (cancelled || !res?.timestamps) return;
        const cols = ['current_1', 'current_2', 'current_3'];
        const names = ['L1', 'L2', 'L3'];
        const colors = ['var(--cyan)', 'var(--amber)', 'var(--purple, #a855f7)'];
        const out: FlowSeries[] = [];
        cols.forEach((key, i) => {
          const ser = (res.series || []).find((x) => x.key === key);
          if (!ser) return;
          const pts: [number, number][] = [];
          for (let j = 0; j < res.timestamps!.length; j++) { const v = ser.data[j]; if (v != null) pts.push([res.timestamps![j], v]); }
          if (pts.length) out.push({ name: names[i], color: colors[i], data: pts });
        });
        setPhaseSeries(out);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.powerSN, th]);
  const setpoint = num('setpoint');
  const oat = num('oat');

  // Condenser water loop (water-cooled): cooling tower ↔ condenser-water pumps ↔ chiller condenser.
  const condS = num('cond_supply');   // cool water leaving tower → entering condenser
  const condR = num('cond_return');   // warm water leaving condenser → back to tower
  const condDt = num('cond_dt') ?? (condS != null && condR != null ? condR - condS : null);
  const condFlow = num('cond_flow');
  const ctFanStatus = num('ct_fan_status');
  const ctFanKw = num('ct_fan_kw');

  // Discover how many chillers / pumps this device describes (1..4).
  const chillers = [1, 2, 3, 4]
    .filter((n) => has(`ch${n}_status`) || has(`ch${n}_kw`) || has(`ch${n}_rt`))
    .map((n) => ({
      id: `CH-${n}`,
      status: num(`ch${n}_status`),
      kw: num(`ch${n}_kw`),
      loadPct: num(`ch${n}_load`),
      rt: num(`ch${n}_rt`),
      kwrt: num(`ch${n}_kwrt`) ?? (num(`ch${n}_kw`) != null && num(`ch${n}_rt`) ? (num(`ch${n}_kw`) as number) / (num(`ch${n}_rt`) as number) : null),
    }));
  // CH-1 kW shows the real chiller power when a power meter is bound.
  if (realPower != null && chillers.length) chillers[0].kw = realPower;
  const pumps = [1, 2, 3, 4]
    .filter((n) => has(`chwp${n}_status`) || has(`chwp${n}_kw`))
    .map((n) => ({ id: `CHWP-${n}`, status: num(`chwp${n}_status`), kw: num(`chwp${n}_kw`) }));
  // Condenser water pumps (CDWP-1..4), discovered the same way as CHW pumps.
  const condPumps = [1, 2, 3, 4]
    .filter((n) => has(`cdwp${n}_status`) || has(`cdwp${n}_kw`))
    .map((n) => ({ id: `CDWP-${n}`, status: num(`cdwp${n}_status`), kw: num(`cdwp${n}_kw`) }));
  const hasCond = has('cond_supply') || has('cond_return') || has('cond_flow') || condPumps.length > 0;

  // ── palette (theme-aware) ──
  const C = {
    cyan: 'var(--cyan)', amber: 'var(--amber)', green: 'var(--green)', red: 'var(--red)',
    blue: 'var(--blue)', gold: 'var(--gold)',
    mut: 'color-mix(in srgb, var(--text-muted) 55%, var(--text-primary))',
    txt: 'var(--text-primary)',
  };
  const BD = 'color-mix(in srgb, var(--text-muted) 45%, transparent)';
  const PANEL = 'var(--bg-deep)';
  const tnum = { fontVariantNumeric: 'tabular-nums' as const };

  const runColor = (s: number | null) => (s == null ? BD : s > 0 ? C.green : C.mut);
  const runText = (s: number | null) => (s == null ? NO_VALUE : s > 0 ? (th ? 'ทำงาน' : 'Run') : (th ? 'หยุด' : 'Stop'));
  const running = chillers.filter((c) => (c.status ?? 0) > 0).length;

  const daily = num('daily');
  const monthly = num('monthly');
  const bahtToday = num('baht_today');
  const bahtMonth = num('baht_month');
  const fmtInt = (v: number | null) => (v == null ? NO_VALUE : Math.round(v).toLocaleString());

  // ── KPI strip — the whole plant summary lives here (self-contained widget).
  // CHW temps/flow live on the schematic below; the strip carries efficiency +
  // energy & cost (today / this month). ──
  const kpis: { label: string; value: string; unit: string; color: string; hero?: boolean }[] = [
    { label: th ? 'ประสิทธิภาพรวม' : 'Plant Efficiency', value: fmt(kwRt, 2), unit: 'kW/RT', color: C.green, hero: true },
    { label: 'COP', value: fmt(cop, 2), unit: '', color: C.blue, hero: true },
    { label: th ? 'โหลดทำความเย็น' : 'Cooling Load', value: fmt(load, 0), unit: 'RT', color: C.cyan },
    { label: th ? 'กำลังไฟรวม' : 'Plant Power', value: fmt(plantKw, 0), unit: 'kW', color: C.amber },
    ...(hasCond ? [{ label: th ? 'ΔT คอนเดนเซอร์' : 'Condenser ΔT', value: fmt(condDt, 1), unit: '°C', color: C.red }] : []),
    { label: th ? 'พลังงานวันนี้' : 'Energy Today', value: fmtInt(daily), unit: 'kWh', color: C.green },
    { label: th ? 'พลังงานเดือนนี้' : 'Energy This Month', value: fmtInt(monthly), unit: 'kWh', color: C.cyan },
    { label: th ? 'ค่าไฟวันนี้' : 'Cost Today', value: fmtInt(bahtToday), unit: '฿', color: C.amber },
    { label: th ? 'ค่าไฟเดือนนี้' : 'Cost This Month', value: fmtInt(bahtMonth), unit: '฿', color: C.gold },
  ];

  // ── 30-day daily demo trends (deterministic — no telemetry table) ───────────
  // The last point of each series is anchored to the live KPI so the chart's
  // right edge matches the number shown above.
  const effTarget = 0.90;
  // Plant efficiency (kW/RT): condenser-fouling hump mid-month (worsens above
  // target) that recovers after a clean near month-end + weekly cycle + noise.
  // chiller_demo passes its own simulated 30-day series (trendEff / trendEnergy)
  const TREND = cfg.trendEff?.length ? cfg.trendEff : (() => {
    const N = 30, vals: number[] = [];
    for (let i = 0; i <= N; i++) {
      const fouling = 0.045 * Math.exp(-Math.pow((i - 18) / 6, 2));
      const weekly = 0.015 * Math.sin((i / 7) * 2 * Math.PI);
      const noise = 0.008 * Math.sin(i * 2.3);
      vals.push(0.85 + fouling + weekly + noise);
    }
    if (kwRt != null) vals[N] = kwRt;
    return vals;
  })();
  // Daily energy (kWh): tracks the hot-spell / fouling period (more energy for the
  // same cooling) with a weekly swing (weekends lower).
  const ENERGY = cfg.trendEnergy?.length ? cfg.trendEnergy : (() => {
    const N = 30, vals: number[] = [];
    for (let i = 0; i <= N; i++) {
      const hot = 200 * Math.exp(-Math.pow((i - 21) / 7, 2));
      const weekly = 90 * Math.sin((i / 7) * 2 * Math.PI - 1);
      const noise = 40 * Math.sin(i * 2.9);
      vals.push(1550 + hot + weekly + noise);
    }
    if (daily != null) vals[N] = daily;
    return vals;
  })();
  // efficiency axis: the old fixed 0.74–0.98 band, widened when a series leaves it
  const effMin = Math.min(0.74, Math.floor((Math.min(...TREND) - 0.02) * 50) / 50);
  const effMax = Math.max(0.98, Math.ceil((Math.max(...TREND) + 0.02) * 50) / 50);
  const eMin = Math.floor((Math.min(...ENERGY) - 60) / 100) * 100;
  const eMax = Math.ceil((Math.max(...ENERGY) + 60) / 100) * 100;
  // x-axis calendar dates: point i is (N - i) days before today.
  const dateLabel = (daysAgo: number) => {
    const d = new Date();
    d.setDate(d.getDate() - daysAgo);
    return d.toLocaleDateString(th ? 'th-TH' : 'en-GB', { day: 'numeric', month: 'short' });
  };

  // Reusable 30-day daily area-line chart (efficiency, energy, …).
  const TrendChart = ({ id, title, extra, series, color, yMin, yMax, yTicks, fmtY, target }: {
    id: string; title: string; extra: React.ReactNode; series: number[]; color: string;
    yMin: number; yMax: number; yTicks: number[]; fmtY: (v: number) => string; target?: number;
  }) => {
    const N = series.length - 1;
    const vw = 1120, vh = 170, mL = 58, mR = 14, mT = 12, mB = 24;
    const pw = vw - mL - mR, ph = vh - mT - mB;
    const xAt = (i: number) => mL + (i / N) * pw;
    const yAt = (v: number) => mT + (1 - (v - yMin) / (yMax - yMin)) * ph;
    const line = series.map((v, i) => `${xAt(i).toFixed(1)},${yAt(v).toFixed(1)}`).join(' ');
    const area = `${line} ${xAt(N).toFixed(1)},${(mT + ph).toFixed(1)} ${xAt(0).toFixed(1)},${(mT + ph).toFixed(1)}`;
    return (
      <div style={{ flex: '1 1 340px', minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 2 }}>
          <span style={{ fontSize: 11.5, fontWeight: 700, color: C.txt, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</span>
          <span style={{ fontSize: 10.5, color: C.mut, whiteSpace: 'nowrap' }}>{extra}</span>
        </div>
        <svg viewBox={`0 0 ${vw} ${vh}`} width="100%" height="auto" style={{ display: 'block' }} preserveAspectRatio="xMidYMid meet">
          <defs>
            <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.32} />
              <stop offset="100%" stopColor={color} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          {yTicks.map((gv, i) => {
            const isT = target != null && gv === target;
            return (
              <g key={i}>
                <line x1={mL} y1={yAt(gv)} x2={vw - mR} y2={yAt(gv)} stroke={isT ? C.gold : BD} strokeWidth={0.8} strokeDasharray={isT ? '5 4' : undefined} strokeOpacity={isT ? 0.7 : 1} />
                <text x={mL - 6} y={yAt(gv) + 3} textAnchor="end" fontSize={9} fill={isT ? C.gold : C.mut}>{fmtY(gv)}</text>
              </g>
            );
          })}
          <polygon points={area} fill={`url(#${id})`} />
          <polyline points={line} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
          <circle cx={xAt(N)} cy={yAt(series[N])} r={3.6} fill={color} stroke="var(--bg-deep)" strokeWidth={1.6} />
          {[0, 10, 20, 30].map((i) => (
            <text key={i} x={xAt(i)} y={vh - 6} textAnchor={i === 0 ? 'start' : i === N ? 'end' : 'middle'} fontSize={9} fill={C.mut}>{dateLabel(N - i)}</text>
          ))}
        </svg>
      </div>
    );
  };

  // ── schematic geometry (data-driven vertical layout for the chillers) ──
  const VB_H = hasCond ? 500 : 360;      // taller when a condenser loop is drawn
  const CH_W = 180, CH_H = 95;
  const CH_X = 70;                       // chiller left edge
  const areaTop = 55, areaBot = 300;     // vertical band the chillers occupy
  const slotH = (areaBot - areaTop) / Math.max(chillers.length, 1);
  const chY = chillers.map((_, i) => areaTop + i * slotH + (slotH - CH_H) / 2);
  const chMid = chY.map((y) => y + CH_H / 2);
  const supplyX = 300;                    // supply riser
  const returnX = 40;                     // return riser
  const returnTopY = 120;                 // top return run height
  const pumpY = 300;                      // pump-station centreline
  const bldgSupplyIn = 215;               // building bottom entry
  // CHW pumps run in PARALLEL: a common inlet header splits into one branch per
  // pump, then recombines into a common outlet header (duty/standby staging).
  const pumpInX = 405, pumpOutX = 700, pumpCX = 545;
  const pumpMids = pumps.map((_, i) => pumpY + (i - (pumps.length - 1) / 2) * 58);
  const pMinY = Math.min(...(pumpMids.length ? pumpMids : [pumpY]));
  const pMaxY = Math.max(...(pumpMids.length ? pumpMids : [pumpY]));

  // ── condenser water loop geometry (bottom band, only when hasCond) ──
  const condReturnY = 380;               // warm CW horizontal run (chiller → tower)
  const condSupplyY = 452;               // cool CW horizontal run (tower → pumps → chiller)
  const chBottomY = (chY.length ? Math.max(...chY) : areaTop) + CH_H;
  const warmTapX = CH_X + 45;            // 115 — condenser warm-outlet riser (to tower)
  const coolTapX = CH_X + 150;           // 220 — condenser cool-inlet riser (from pumps)
  const towerX = 890, towerW = 185, towerY = 360, towerH = 118;
  const cdInX = 660, cdOutX = 468, cdCX = 564;   // condenser-pump parallel headers + centre
  const cdMids = condPumps.map((_, i) => condSupplyY + (i - (condPumps.length - 1) / 2) * 30);
  const cdMinY = Math.min(...(cdMids.length ? cdMids : [condSupplyY]));
  const cdMaxY = Math.max(...(cdMids.length ? cdMids : [condSupplyY]));

  const St = { fontFamily: 'system-ui, sans-serif' } as const;

  // Demo Supply/Return temperature comparison (the flow meters have no temp
  // channel) — deterministic 24h synthetic trend around chw_supply / chw_return.
  const tempDemo = (() => {
    const cats: string[] = [], sup: number[] = [], ret: number[] = [];
    const baseS = chwS ?? 7, baseR = chwR ?? 12.8;
    for (let h = 0; h < 24; h++) {
      cats.push(`${String(h).padStart(2, '0')}:00`);
      sup.push(+(baseS + Math.sin(h / 3.8) * 0.25).toFixed(1));
      ret.push(+(baseR + Math.sin(h / 4.2 + 1) * 0.4).toFixed(1));
    }
    return { cats, sup, ret };
  })();

  // Shared area-chart renderer for the bottom comparison charts.
  const renderArea = (
    series: { name: string; color: string; data: number[] | [number, number][] }[],
    o: { unit: string; datetime?: boolean; categories?: string[] }
  ) => {
    if (series.length === 0) return <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.mut, fontSize: 11 }}>{th ? 'กำลังโหลด…' : 'Loading…'}</div>;
    const dark = isDarkTheme();
    const axisCol = dark ? '#94a3b8' : '#64748b';
    const options: ApexCharts.ApexOptions = {
      chart: { type: 'area', toolbar: { show: false }, background: 'transparent' },
      colors: series.map((s) => s.color),
      stroke: { curve: 'smooth', width: 2 },
      fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.15, opacityTo: 0.02, stops: [0, 100] } },
      dataLabels: { enabled: false },
      xaxis: o.datetime
        ? { type: 'datetime', labels: { style: { colors: axisCol, fontSize: '9px' }, datetimeUTC: true, datetimeFormatter: { hour: 'HH:mm' } }, axisBorder: { show: false }, axisTicks: { show: false } }
        : { categories: o.categories || [], labels: { style: { colors: axisCol, fontSize: '9px' }, rotate: 0, hideOverlappingLabels: true }, axisBorder: { show: false }, axisTicks: { show: false } },
      yaxis: { labels: { style: { colors: axisCol, fontSize: '9px' }, formatter: (v: number) => v.toFixed(v < 10 ? 1 : 0) } },
      grid: { borderColor: dark ? 'rgba(148,163,184,0.12)' : 'rgba(148,163,184,0.15)', strokeDashArray: 3 },
      legend: { show: true, position: 'top', horizontalAlign: 'right', fontSize: '10px', labels: { colors: axisCol }, markers: { size: 6 } },
      tooltip: { theme: dark ? 'dark' : 'light', shared: true, ...(o.datetime ? { x: { format: 'HH:mm' } } : {}), y: { formatter: (v: number) => `${v.toFixed(o.unit === '°C' ? 1 : 2)} ${o.unit}` } },
    };
    return <ApexChartWrapper options={options} series={series.map((s) => ({ name: s.name, data: s.data }))} type="area" height="100%" />;
  };

  // Multi-metric renderer: include any of flow (l/m) / temperature (°C) / power
  // (kW), each on its own y-axis so the differing scales don't collide.
  const renderMetrics = (opts: { flow?: boolean; temp?: boolean; current?: boolean }) => {
    const flowS = opts.flow ? flowSeries : [];
    const tempS = opts.temp && tempChart[0] ? [{ ...tempChart[0], color: 'var(--green)' }] : [];
    const curS = opts.current ? phaseSeries : [];
    const all = [...flowS, ...tempS, ...curS];
    if (all.length === 0) return <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.mut, fontSize: 11 }}>{th ? 'กำลังโหลด…' : 'Loading…'}</div>;
    const dark = isDarkTheme();
    const axisCol = dark ? '#94a3b8' : '#64748b';
    const flowNames = flowS.map((s) => s.name);
    const tempNames = tempS.map((s) => s.name);
    const curNames = curS.map((s) => s.name);
    const axisStyle = { style: { color: axisCol, fontSize: '9px' } };
    const lblStyle = (fn: (v: number) => string) => ({ style: { colors: axisCol, fontSize: '9px' }, formatter: fn });
    const yaxis = [
      ...(flowNames.length ? [{ seriesName: flowNames, title: { text: flowUnit, ...axisStyle }, labels: lblStyle((v) => v.toFixed(1)) }] : []),
      ...(tempNames.length ? [{ seriesName: tempNames, opposite: true, title: { text: '°C', ...axisStyle }, labels: lblStyle((v) => v.toFixed(0)) }] : []),
      ...(curNames.length ? [{ seriesName: curNames, title: { text: 'A', ...axisStyle }, labels: lblStyle((v) => v.toFixed(1)) }] : []),
    ];
    const options: ApexCharts.ApexOptions = {
      chart: { type: 'area', toolbar: { show: false }, background: 'transparent' },
      colors: all.map((s) => s.color),
      stroke: { curve: 'smooth', width: 2 },
      fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.08, opacityTo: 0.02, stops: [0, 100] } },
      dataLabels: { enabled: false },
      xaxis: { type: 'datetime', labels: { style: { colors: axisCol, fontSize: '9px' }, datetimeUTC: true, datetimeFormatter: { hour: 'HH:mm' } }, axisBorder: { show: false }, axisTicks: { show: false } },
      yaxis,
      grid: { borderColor: dark ? 'rgba(148,163,184,0.12)' : 'rgba(148,163,184,0.15)', strokeDashArray: 3 },
      legend: { show: true, position: 'top', horizontalAlign: 'center', fontSize: '10px', labels: { colors: axisCol }, markers: { size: 6 }, itemMargin: { horizontal: 7 } },
      tooltip: { theme: dark ? 'dark' : 'light', shared: true, x: { format: 'HH:mm' } },
    };
    return <ApexChartWrapper options={options} series={all.map((s) => ({ name: s.name, data: s.data }))} type="area" height="100%" />;
  };

  // widget.style 'illustrated' → real-machine scene (TOA look) + side panel,
  // the two 30-day trends as a full-width row under both (ScadaShell below).
  if (cfg.style === 'illustrated') {
    const on = (v: number | null) => (v ?? 0) > 0;
    const ch = chillers[0];
    const pts: { name: string; sub: string; on: boolean; value: string }[] = [
      ...chillers.map((c) => ({ name: c.id, sub: th ? 'ชิลเลอร์' : 'Chiller', on: on(c.status), value: `${fmt(c.kw, 0)} kW` })),
      ...pumps.map((q) => ({ name: q.id, sub: th ? 'ปั๊มน้ำเย็น' : 'CHW pump', on: on(q.status), value: on(q.status) ? `${fmt(q.kw)} kW` : (th ? 'สำรอง' : 'standby') })),
      ...condPumps.map((q) => ({ name: q.id, sub: th ? 'ปั๊มน้ำระบายความร้อน' : 'CDW pump', on: on(q.status), value: on(q.status) ? `${fmt(q.kw)} kW` : (th ? 'สำรอง' : 'standby') })),
      ...(ctFanStatus != null ? [{ name: 'CT-1', sub: th ? 'พัดลมคูลลิ่งทาวเวอร์' : 'Cooling-tower fan', on: on(ctFanStatus), value: `${fmt(ctFanKw)} kW` }] : []),
    ];
    const tile = (k: string, v: string, unit: string, c?: string) => (
      <div key={k} style={{ padding: '8px 10px', borderRadius: 8, border: `1px solid ${K.edge}`, background: mix(K.txt, 3), fontSize: 11, color: K.mut }}>
        <div>{k}</div>
        <div style={{ fontSize: 15, fontWeight: 800, color: c ?? K.txt, fontVariantNumeric: 'tabular-nums' }}>{v}<span style={{ fontSize: 10.5, fontWeight: 600, color: K.mut, marginLeft: 3 }}>{unit}</span></div>
      </div>
    );
    return (
      <ScadaBelowContext.Provider value={
        <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'stretch' }}>
          <TrendCard id="chl-eff" th={th} title={th ? 'ประสิทธิภาพรวม' : 'Plant efficiency'} sub={th ? 'kW/RT · รายวัน 30 วัน · ยิ่งต่ำยิ่งดี' : 'kW/RT · daily, 30 days · lower is better'}
            unit="kW/RT" series={TREND} labels={TREND.map((_, i) => dateLabel(TREND.length - 1 - i))} color="#10b981"
            fmt={(v) => v.toFixed(2)} target={effTarget} lowerIsBetter />
          <TrendCard id="chl-kwh" th={th} title={th ? 'พลังงานรายวัน' : 'Daily energy'} sub={th ? 'kWh · 30 วัน' : 'kWh · 30 days'}
            unit="kWh" series={ENERGY} labels={ENERGY.map((_, i) => dateLabel(ENERGY.length - 1 - i))} color="#f59e0b"
            fmt={(v) => Math.round(v).toLocaleString()} lowerIsBetter />
        </div>
      }>
        <ScadaShell
          drawing={<ChillerPlantScadaView th={th} online={online}
            chiller={{ on: on(ch?.status ?? null), kw: ch?.kw ?? null, load: ch?.loadPct ?? null, rt: ch?.rt ?? null, kwrt: ch?.kwrt ?? null }}
            chwp={pumps.map((q) => ({ on: on(q.status), kw: q.kw }))}
            cdwp={condPumps.map((q) => ({ on: on(q.status), kw: q.kw }))}
            tower={{ on: on(ctFanStatus), kw: ctFanKw }}
            chwS={chwS} chwR={chwR} chwFlow={supplyFlow} setpoint={setpoint}
            cdS={condS} cdR={condR} cdFlow={condFlow} coolingLoad={load} />}
          side={<>
            <SideHead title={th ? 'อุปกรณ์' : 'Equipment'} right={`${pts.filter((x) => x.on).length}/${pts.length} ${th ? 'ทำงาน' : 'running'}`} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {pts.map((x) => {
                const health: Health = !online ? 'offline' : x.on ? 'ok' : 'pending';
                return <MachineRow key={x.name} name={x.name} sub={x.sub} health={health} active={false} onClick={() => {}}
                  status={!online ? (th ? 'ออฟไลน์' : 'Offline') : x.on ? (th ? 'ทำงาน' : 'Running') : (th ? 'สำรอง' : 'Standby')} values={[x.value]} />;
              })}
            </div>
            <SideHead title={th ? 'ประสิทธิภาพ' : 'Efficiency'} right={oat != null ? `${th ? 'อากาศภายนอก' : 'outdoor'} ${fmt(oat)} °C` : undefined} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {tile(th ? 'ประสิทธิภาพรวม' : 'Plant efficiency', fmt(kwRt, 2), 'kW/RT', kwRt != null && kwRt > effTarget ? K.amber : K.green)}
              {tile('COP', fmt(cop, 2), '')}
              {tile(th ? 'กำลังไฟรวม' : 'Plant power', fmt(plantKw, 0), 'kW')}
              {tile(th ? 'โหลดความเย็น' : 'Cooling load', fmt(load, 0), 'RT')}
            </div>
            <SideHead title={th ? 'พลังงาน · ค่าไฟ' : 'Energy · cost'} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {tile(th ? 'พลังงานวันนี้' : 'Energy today', fmtInt(daily), 'kWh')}
              {tile(th ? 'ค่าไฟวันนี้' : 'Cost today', fmtInt(bahtToday), '฿', K.amber)}
              {tile(th ? 'พลังงานเดือนนี้' : 'Energy this month', fmtInt(monthly), 'kWh')}
              {tile(th ? 'ค่าไฟเดือนนี้' : 'Cost this month', fmtInt(bahtMonth), '฿', K.amber)}
            </div>
          </>}
        />
      </ScadaBelowContext.Provider>
    );
  }

  return (
    <div style={{ ...St, position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', gap: 10, containerType: 'inline-size' }}>
      <style>{`
        .chl-flow { stroke-dasharray: 7 9; animation: chlMove 1.1s linear infinite; }
        .chl-flow.rev { animation-direction: reverse; }
        @keyframes chlMove { to { stroke-dashoffset: -16; } }
        .chl-fan { transform-box: fill-box; transform-origin: center; animation: chlSpin 2.6s linear infinite; }
        @keyframes chlSpin { to { transform: rotate(360deg); } }
        .chl-pulse { animation: chlPulse 1.5s ease-in-out infinite; }
        @keyframes chlPulse { 0%,100% { opacity: 1; } 50% { opacity: .35; } }
        @media (prefers-reduced-motion: reduce){ .chl-flow,.chl-fan,.chl-pulse { animation: none; } }
        .chl-idle .chl-flow,.chl-idle .chl-fan { animation: none; }
      `}</style>

      {/* ── header (also carries device status — replaces the standalone list) ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span className={online ? 'chl-pulse' : ''} style={{ width: 8, height: 8, borderRadius: 99, background: online ? C.green : C.red, boxShadow: online ? `0 0 6px ${C.green}` : 'none' }} />
        <span style={{ fontSize: 12.5, fontWeight: 700, color: C.txt }}>{online ? (th ? 'ระบบทำงานปกติ' : 'System Online') : (th ? 'ออฟไลน์' : 'Offline')}</span>
        <span style={{ fontSize: 11, color: C.mut }}>· {online ? running : NO_VALUE}/{chillers.length} {th ? 'ชิลเลอร์ทำงาน' : 'chillers running'}</span>
        {oat != null && <span style={{ fontSize: 11, color: C.mut }}>· {th ? 'อากาศภายนอก' : 'Outdoor'} {fmt(oat, 1)}°C</span>}
        {cfg.demo && (
          <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 10, fontWeight: 800, color: C.amber, background: 'color-mix(in srgb, var(--amber) 15%, transparent)', border: `1px solid color-mix(in srgb, var(--amber) 45%, transparent)`, padding: '2px 9px', borderRadius: 99 }}>
            {th ? '⚠ ข้อมูลตัวอย่าง (DEMO)' : '⚠ DEMO DATA'}
            {(() => {
              const live = [liveFlow && (th ? 'Flow' : 'Flow'), livePower && (th ? 'Power' : 'Power'), liveTemp && (th ? 'อุณหภูมิ' : 'Temp')].filter(Boolean);
              return live.length > 0 ? <span style={{ color: C.green, fontWeight: 700 }}>· {live.join(', ')} = {th ? 'ค่าจริง' : 'live'}</span> : null;
            })()}
          </span>
        )}
      </div>

      {/* ── KPI strip (hidden when widget.hideKpis) ── */}
      {!cfg.hideKpis && (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
        {kpis.map((k, i) => (
          <div key={i} style={{ background: PANEL, border: `1px solid ${k.hero ? k.color : BD}`, borderRadius: 10, padding: '8px 11px' }}>
            <div style={{ fontSize: 10.5, color: C.mut, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{k.label}</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 2 }}>
              <span style={{ fontSize: k.hero ? 24 : 20, fontWeight: 800, color: k.color, letterSpacing: '-0.01em', ...tnum }}>{k.value}</span>
              {k.unit && <span style={{ fontSize: 10.5, color: C.mut, fontWeight: 600 }}>{k.unit}</span>}
            </div>
          </div>
        ))}
      </div>
      )}

      {/* ── SCADA schematic ── */}
      {/* offline: the flows/impellers stop — the plant's state is unknown */}
      <svg className={online ? undefined : 'chl-idle'} viewBox={`0 0 1120 ${VB_H}`} width="100%" style={{ display: 'block', width: '100%', height: 'auto', maxHeight: '100%' }} preserveAspectRatio="xMidYMin meet">
        {/* CHW SUPPLY (cold, cyan): chiller stubs → riser → PARALLEL pumps → building */}
        <g>
          {chMid.map((my, i) => (
            <g key={`ss${i}`}>
              <line x1={CH_X + CH_W} y1={my} x2={supplyX} y2={my} stroke={C.cyan} strokeWidth={7} strokeOpacity={0.28} strokeLinecap="round" />
              <line className="chl-flow" x1={CH_X + CH_W} y1={my} x2={supplyX} y2={my} stroke={C.cyan} strokeWidth={2.6} strokeLinecap="round" />
            </g>
          ))}
          {/* riser + run into the pump-station inlet header */}
          <polyline points={`${supplyX},${Math.min(...chMid)} ${supplyX},${pumpY} ${pumpInX},${pumpY}`} fill="none" stroke={C.cyan} strokeWidth={7} strokeOpacity={0.28} strokeLinecap="round" strokeLinejoin="round" />
          <polyline className="chl-flow" points={`${supplyX},${Math.min(...chMid)} ${supplyX},${pumpY} ${pumpInX},${pumpY}`} fill="none" stroke={C.cyan} strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" />
          {/* common inlet / outlet headers (vertical) — only when >1 pump */}
          {pumps.length > 1 && (
            <>
              <line x1={pumpInX} y1={pMinY} x2={pumpInX} y2={pMaxY} stroke={C.cyan} strokeWidth={7} strokeOpacity={0.28} strokeLinecap="round" />
              <line x1={pumpOutX} y1={pMinY} x2={pumpOutX} y2={pMaxY} stroke={C.cyan} strokeWidth={7} strokeOpacity={0.28} strokeLinecap="round" />
            </>
          )}
          {/* one parallel branch per pump (flow only on running pumps) */}
          {pumpMids.map((by, i) => {
            const on = (pumps[i].status ?? 0) > 0;
            return (
              <g key={`pb${i}`}>
                <line x1={pumpInX} y1={by} x2={pumpOutX} y2={by} stroke={C.cyan} strokeWidth={7} strokeOpacity={0.28} strokeLinecap="round" />
                {on && <line className="chl-flow" x1={pumpInX} y1={by} x2={pumpOutX} y2={by} stroke={C.cyan} strokeWidth={2.6} strokeLinecap="round" />}
              </g>
            );
          })}
          {/* run from outlet header to the building */}
          <polyline points={`${pumpOutX},${pumpY} 980,${pumpY} 980,${bldgSupplyIn}`} fill="none" stroke={C.cyan} strokeWidth={7} strokeOpacity={0.28} strokeLinecap="round" strokeLinejoin="round" />
          <polyline className="chl-flow" points={`${pumpOutX},${pumpY} 980,${pumpY} 980,${bldgSupplyIn}`} fill="none" stroke={C.cyan} strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" />
        </g>

        {/* CHW RETURN (warm, amber): building → top run → riser → chiller stubs */}
        <g>
          <polyline points={`885,${returnTopY} ${returnX},${returnTopY} ${returnX},${Math.max(...chMid)}`} fill="none" stroke={C.amber} strokeWidth={7} strokeOpacity={0.26} strokeLinecap="round" strokeLinejoin="round" />
          <polyline className="chl-flow rev" points={`${returnX},${Math.max(...chMid)} ${returnX},${returnTopY} 885,${returnTopY}`} fill="none" stroke={C.amber} strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" />
          {chMid.map((my, i) => (
            <g key={`rs${i}`}>
              <line x1={returnX} y1={my} x2={CH_X} y2={my} stroke={C.amber} strokeWidth={7} strokeOpacity={0.26} strokeLinecap="round" />
              <line className="chl-flow" x1={returnX} y1={my} x2={CH_X} y2={my} stroke={C.amber} strokeWidth={2.6} strokeLinecap="round" />
            </g>
          ))}
        </g>

        {/* ── CONDENSER WATER LOOP (water-cooled): chiller ⇄ CDWP pumps ⇄ cooling tower ── */}
        {hasCond && (
          <g>
            {/* warm CW (red): condenser out → down → across → into tower top */}
            {[0.24, 1].map((op, k) => (
              <polyline key={`cw-w${k}`} className={k ? 'chl-flow' : ''}
                points={`${warmTapX},${chBottomY} ${warmTapX},${condReturnY} ${towerX + 45},${condReturnY} ${towerX + 45},${towerY + 18}`}
                fill="none" stroke={C.red} strokeWidth={k ? 2.6 : 7} strokeOpacity={k ? 1 : op} strokeLinecap="round" strokeLinejoin="round" />
            ))}
            {/* cool CW (blue): tower basin → across → pumps → up → condenser in */}
            {[0.24, 1].map((op, k) => (
              <polyline key={`cw-c1${k}`} className={k ? 'chl-flow rev' : ''}
                points={`${towerX + towerW - 45},${towerY + towerH - 14} ${towerX + towerW - 45},${condSupplyY} ${cdInX},${condSupplyY}`}
                fill="none" stroke={C.blue} strokeWidth={k ? 2.6 : 7} strokeOpacity={k ? 1 : op} strokeLinecap="round" strokeLinejoin="round" />
            ))}
            {[0.24, 1].map((op, k) => (
              <polyline key={`cw-c2${k}`} className={k ? 'chl-flow rev' : ''}
                points={`${cdOutX},${condSupplyY} ${coolTapX},${condSupplyY} ${coolTapX},${chBottomY}`}
                fill="none" stroke={C.blue} strokeWidth={k ? 2.6 : 7} strokeOpacity={k ? 1 : op} strokeLinecap="round" strokeLinejoin="round" />
            ))}
            {/* parallel condenser-pump headers + one branch per pump */}
            {condPumps.length > 1 && (
              <>
                <line x1={cdInX} y1={cdMinY} x2={cdInX} y2={cdMaxY} stroke={C.blue} strokeWidth={7} strokeOpacity={0.24} strokeLinecap="round" />
                <line x1={cdOutX} y1={cdMinY} x2={cdOutX} y2={cdMaxY} stroke={C.blue} strokeWidth={7} strokeOpacity={0.24} strokeLinecap="round" />
              </>
            )}
            {cdMids.map((by, i) => {
              const on = (condPumps[i].status ?? 0) > 0;
              return (
                <g key={`cdb${i}`}>
                  <line x1={cdOutX} y1={by} x2={cdInX} y2={by} stroke={C.blue} strokeWidth={7} strokeOpacity={0.24} strokeLinecap="round" />
                  {on && <line className="chl-flow rev" x1={cdOutX} y1={by} x2={cdInX} y2={by} stroke={C.blue} strokeWidth={2.6} strokeLinecap="round" />}
                </g>
              );
            })}
            {/* pipe labels + temps */}
            <g fontSize={11} fontWeight={700}>
              <rect x={warmTapX + 12} y={condReturnY - 20} width={16} height={4} rx={2} fill={C.red} />
              <text x={warmTapX + 34} y={condReturnY - 16} fill={C.mut}>{th ? 'น้ำระบายความร้อนกลับ (Cond. Return)' : 'Cond. Return'} {condR != null ? `${fmt(condR, 1)}°C` : ''}</text>
              <rect x={coolTapX + 12} y={condSupplyY + 12} width={16} height={4} rx={2} fill={C.blue} />
              <text x={coolTapX + 34} y={condSupplyY + 16} fill={C.mut}>{th ? 'น้ำระบายความร้อนจ่าย (Cond. Supply)' : 'Cond. Supply'} {condS != null ? `${fmt(condS, 1)}°C` : ''}</text>
            </g>
            {condFlow != null && (
              <g fontWeight={800}>
                <rect x={(warmTapX + towerX) / 2 - 41} y={condReturnY - 11} width={82} height={22} rx={6} fill={PANEL} stroke={C.red} strokeOpacity={0.7} />
                <text x={(warmTapX + towerX) / 2} y={condReturnY + 4} textAnchor="middle" fontSize={10.5} fill={C.red}>▸ {fmt(condFlow, 0)} m³/h</text>
              </g>
            )}
          </g>
        )}

        {/* pipe legend */}
        <g fontSize={11} fontWeight={700}>
          <rect x={340} y={322} width={16} height={4} rx={2} fill={C.cyan} />
          <text x={362} y={328} fill={C.mut}>{th ? 'น้ำเย็นจ่าย (Supply)' : 'CHW Supply'} {chwS != null ? `${fmt(chwS, 1)}°C` : ''}</text>
          <rect x={560} y={132} width={16} height={4} rx={2} fill={C.amber} />
          <text x={582} y={138} fill={C.mut}>{th ? 'น้ำเย็นกลับ (Return)' : 'CHW Return'} {chwR != null ? `${fmt(chwR, 1)}°C` : ''}</text>
        </g>

        {/* flow-rate tags on the pipes (▾ supply on the riser · ◂ return on the top run).
            When bound to real flow meters these show live values (green LIVE dot). */}
        <g fontWeight={800}>
          {supplyFlow != null && (<>
            <rect x={supplyX + 8} y={(Math.min(...chMid) + pumpY) / 2 - 11} width={liveFlow ? 90 : 80} height={22} rx={6} fill={PANEL} stroke={C.cyan} strokeOpacity={0.7} />
            {liveFlow && <circle cx={supplyX + 18} cy={(Math.min(...chMid) + pumpY) / 2} r={3} fill={C.green} />}
            <text x={supplyX + (liveFlow ? 54 : 48)} y={(Math.min(...chMid) + pumpY) / 2 + 4} textAnchor="middle" fontSize={10.5} fill={C.cyan}>▾ {fmtFlow(supplyFlow)} {flowUnit}</text>
          </>)}
          {returnFlow != null && (<>
            <rect x={392} y={returnTopY - 11} width={liveFlow ? 90 : 80} height={22} rx={6} fill={PANEL} stroke={C.amber} strokeOpacity={0.7} />
            {liveFlow && <circle cx={402} cy={returnTopY} r={3} fill={C.green} />}
            <text x={392 + (liveFlow ? 49 : 40)} y={returnTopY + 4} textAnchor="middle" fontSize={10.5} fill={C.amber}>◂ {fmtFlow(returnFlow)} {flowUnit}</text>
          </>)}
        </g>

        {/* ── Chillers (air-cooled: fans on top) ── */}
        {chillers.map((c, i) => {
          const y = chY[i];
          const on = (c.status ?? 0) > 0;
          const col = runColor(c.status);
          return (
            <g key={c.id}>
              <rect x={CH_X} y={y} width={CH_W} height={CH_H} rx={10} fill={PANEL} stroke={col} strokeWidth={on ? 2 : 1.3} />
              {/* fan grilles (air-cooled) — plain vents, no spinning star */}
              {[CH_X + 42, CH_X + 108].map((cx) => (
                <g key={cx}>
                  <circle cx={cx} cy={y + 26} r={19} fill="none" stroke={on ? col : BD} strokeWidth={1.4} strokeOpacity={on ? 0.8 : 1} />
                  <circle cx={cx} cy={y + 26} r={11} fill="none" stroke={on ? col : BD} strokeWidth={1} strokeOpacity={0.5} />
                  <circle cx={cx} cy={y + 26} r={3} fill={on ? col : C.mut} />
                </g>
              ))}
              <text x={CH_X + CH_W / 2} y={y + 63} textAnchor="middle" fontSize={13} fontWeight={800} fill={C.txt}>{c.id}</text>
              <text x={CH_X + CH_W / 2} y={y + 78} textAnchor="middle" fontSize={9.5} fill={C.mut}>{th ? (hasCond ? 'ชิลเลอร์ระบายความร้อนด้วยน้ำ' : 'ชิลเลอร์ระบายความร้อนด้วยอากาศ') : (hasCond ? 'Water-Cooled Chiller' : 'Air-Cooled Chiller')}</text>
              <circle cx={CH_X + 14} cy={y + 12} r={4} fill={col} />
              <text x={CH_X + 24} y={y + 15.5} fontSize={9.5} fontWeight={700} fill={col}>{runText(c.status)}</text>
              <text x={CH_X + CH_W - 14} y={y + 15.5} textAnchor="end" fontSize={11} fontWeight={800} fill={C.amber} style={tnum}>{fmt(c.kw, 0)} kW</text>
              {c.loadPct != null && <text x={CH_X} y={y + CH_H + 16} fontSize={10} fill={C.mut}>{th ? 'โหลด' : 'Load'} <tspan fontWeight={800} fill={C.txt}>{fmt(c.loadPct, 0)}%</tspan></text>}
              {c.rt != null && <text x={CH_X + CH_W / 2} y={y + CH_H + 16} textAnchor="middle" fontSize={10} fill={C.mut}>{fmt(c.rt, 0)} <tspan fill={C.cyan} fontWeight={700}>RT</tspan></text>}
              {c.kwrt != null && <text x={CH_X + CH_W} y={y + CH_H + 16} textAnchor="end" fontSize={10} fill={C.mut}><tspan fontWeight={800} fill={c.kwrt <= 0.85 ? C.green : C.txt}>{fmt(c.kwrt, 2)}</tspan> kW/RT</text>}
            </g>
          );
        })}

        {/* ── CHW Pumps — parallel branches (duty/standby) ── */}
        <text x={(pumpInX + pumpOutX) / 2} y={pMinY - 34} textAnchor="middle" fontSize={10.5} fontWeight={700} fill={C.mut}>{th ? 'ปั๊มน้ำเย็น · ต่อขนาน' : 'CHW Pumps · Parallel'}</text>
        {pumps.map((p, i) => {
          const cx = pumpCX;
          const cy = pumpMids[i];
          const on = (p.status ?? 0) > 0;
          const col = runColor(p.status);
          // centrifugal pump: motor on top · volute casing · base feet · spinning impeller
          return (
            <g key={p.id}>
              {/* base plate + feet */}
              <rect x={cx - 14} y={cy + 12} width={28} height={4} rx={1.5} fill={col} opacity={0.55} />
              <rect x={cx - 11} y={cy + 16} width={5} height={3} rx={1} fill={col} opacity={0.55} />
              <rect x={cx + 6} y={cy + 16} width={5} height={3} rx={1} fill={col} opacity={0.55} />
              {/* motor + coupling */}
              <rect x={cx - 8} y={cy - 25} width={16} height={9} rx={2} fill={PANEL} stroke={col} strokeWidth={1.2} />
              <line x1={cx} y1={cy - 16} x2={cx} y2={cy - 12} stroke={col} strokeWidth={2} strokeLinecap="round" />
              {/* volute casing */}
              <circle cx={cx} cy={cy} r={12} fill={PANEL} stroke={col} strokeWidth={on ? 2 : 1.3} />
              {/* impeller — spins about its own centre via .chl-fan (fill-box) */}
              <g className={on ? 'chl-fan' : ''}>
                <path d={`M ${cx} ${cy - 7} A 7 7 0 0 1 ${cx + 7} ${cy}`} fill="none" stroke={col} strokeWidth={2} strokeLinecap="round" />
                <path d={`M ${cx} ${cy + 7} A 7 7 0 0 1 ${cx - 7} ${cy}`} fill="none" stroke={col} strokeWidth={2} strokeLinecap="round" />
                <circle cx={cx} cy={cy} r={2} fill={col} />
              </g>
              <text x={cx + 22} y={cy + 3.5} fontSize={10} fill={C.mut}>
                <tspan fontWeight={800} fill={C.txt}>{p.id}</tspan> · {on ? `${fmt(p.kw, 1)} kW` : p.status == null ? NO_VALUE : (th ? 'สำรอง' : 'Standby')}
              </text>
            </g>
          );
        })}

        {/* ── Cooling Tower + Condenser Water Pumps (water-cooled) ── */}
        {hasCond && (
          <g>
            {(() => {
              const on = (ctFanStatus ?? (condPumps.some((p) => (p.status ?? 0) > 0) ? 1 : 0)) > 0;
              const col = runColor(ctFanStatus ?? (on ? 1 : 0));
              const cx = towerX + towerW / 2;
              return (
                <g>
                  <rect x={towerX} y={towerY} width={towerW} height={towerH} rx={12} fill={PANEL} stroke={col} strokeWidth={on ? 2 : 1.3} />
                  {/* induced-draft fan on top */}
                  <circle cx={cx} cy={towerY + 2} r={20} fill={PANEL} stroke={col} strokeWidth={1.4} />
                  <g className={on ? 'chl-fan' : ''} style={{ transformBox: 'fill-box', transformOrigin: 'center' }}>
                    {[0, 120, 240].map((a) => (
                      <line key={a} x1={cx} y1={towerY + 2} x2={cx + 15 * Math.cos((a * Math.PI) / 180)} y2={towerY + 2 + 15 * Math.sin((a * Math.PI) / 180)} stroke={col} strokeWidth={2.4} strokeLinecap="round" />
                    ))}
                    <circle cx={cx} cy={towerY + 2} r={2.5} fill={col} />
                  </g>
                  {/* fill louvers */}
                  <g stroke={col} strokeWidth={1} opacity={0.45}>
                    {[0, 1, 2, 3].map((k) => (
                      <line key={k} x1={towerX + 16} y1={towerY + 36 + k * 13} x2={towerX + towerW - 16} y2={towerY + 36 + k * 13} />
                    ))}
                  </g>
                  {/* cold-water basin */}
                  <rect x={towerX + 12} y={towerY + towerH - 18} width={towerW - 24} height={11} rx={3} fill={C.blue} opacity={0.2} />
                  <text x={cx} y={towerY + towerH + 15} textAnchor="middle" fontSize={11} fontWeight={800} fill={C.txt}>{th ? 'หอผึ่งน้ำ (Cooling Tower)' : 'Cooling Tower'}</text>
                  <text x={towerX + towerW - 14} y={towerY + 16} textAnchor="end" fontSize={9.5} fontWeight={700} fill={col}>{runText(ctFanStatus)}{ctFanKw != null ? ` · ${fmt(ctFanKw, 1)} kW` : ''}</text>
                </g>
              );
            })()}
            {/* Condenser water pumps (parallel duty/standby) */}
            <text x={cdCX} y={cdMinY - 24} textAnchor="middle" fontSize={10.5} fontWeight={700} fill={C.mut}>{th ? 'ปั๊มน้ำระบายความร้อน · ต่อขนาน' : 'Condenser Pumps · Parallel'}</text>
            {condPumps.map((p, i) => {
              const cx = cdCX;
              const cy = cdMids[i];
              const on = (p.status ?? 0) > 0;
              const col = runColor(p.status);
              return (
                <g key={p.id}>
                  <rect x={cx - 8} y={cy - 25} width={16} height={9} rx={2} fill={PANEL} stroke={col} strokeWidth={1.2} />
                  <line x1={cx} y1={cy - 16} x2={cx} y2={cy - 12} stroke={col} strokeWidth={2} strokeLinecap="round" />
                  <circle cx={cx} cy={cy} r={12} fill={PANEL} stroke={col} strokeWidth={on ? 2 : 1.3} />
                  <g className={on ? 'chl-fan' : ''}>
                    <path d={`M ${cx} ${cy - 7} A 7 7 0 0 1 ${cx + 7} ${cy}`} fill="none" stroke={col} strokeWidth={2} strokeLinecap="round" />
                    <path d={`M ${cx} ${cy + 7} A 7 7 0 0 1 ${cx - 7} ${cy}`} fill="none" stroke={col} strokeWidth={2} strokeLinecap="round" />
                    <circle cx={cx} cy={cy} r={2} fill={col} />
                  </g>
                  <text x={cx + 22} y={cy + 3.5} fontSize={10} fill={C.mut}>
                    <tspan fontWeight={800} fill={C.txt}>{p.id}</tspan> · {on ? `${fmt(p.kw, 1)} kW` : p.status == null ? NO_VALUE : (th ? 'สำรอง' : 'Standby')}
                  </text>
                </g>
              );
            })}
          </g>
        )}

        {/* ── cooling load node ── machine/equipment (widget.loadName) or Building/AHU ── */}
        <g>
          <rect x={880} y={95} width={195} height={120} rx={12} fill={PANEL} stroke={C.blue} strokeWidth={1.6} />
          {cfg.loadName ? (
            // machine / equipment — gear glyph
            <g stroke={C.blue} fill="none" opacity={0.9}>
              {Array.from({ length: 8 }).map((_, i) => {
                const a = (i * 45) * Math.PI / 180, cx = 932, cy = 155;
                return <line key={i} x1={cx + 21 * Math.cos(a)} y1={cy + 21 * Math.sin(a)} x2={cx + 30 * Math.cos(a)} y2={cy + 30 * Math.sin(a)} strokeWidth={3.2} strokeLinecap="round" />;
              })}
              <circle cx={932} cy={155} r={21} strokeWidth={2} />
              <circle cx={932} cy={155} r={8} strokeWidth={2} />
            </g>
          ) : (
            // building windows
            <g stroke={C.blue} strokeWidth={1.5} fill="none" opacity={0.9}>
              <rect x={905} y={120} width={44} height={70} rx={3} />
              <rect x={958} y={135} width={40} height={55} rx={3} />
              <line x1={915} y1={135} x2={939} y2={135} /><line x1={915} y1={150} x2={939} y2={150} /><line x1={915} y1={165} x2={939} y2={165} />
              <line x1={967} y1={150} x2={989} y2={150} /><line x1={967} y1={165} x2={989} y2={165} />
            </g>
          )}
          <text x={1042} y={130} textAnchor="end" fontSize={12.5} fontWeight={800} fill={C.txt}>{cfg.loadName || (th ? 'อาคาร / AHU' : 'Building / AHU')}</text>
          {load != null && (
            <>
              <text x={1042} y={147} textAnchor="end" fontSize={10} fill={C.mut}>{th ? 'โหลดทำความเย็น' : 'Cooling Load'}</text>
              <text x={1042} y={182} textAnchor="end" fontSize={22} fontWeight={800} fill={C.cyan} style={tnum}>{fmt(load, 0)}</text>
              <text x={1042} y={198} textAnchor="end" fontSize={10.5} fill={C.mut}>RT</text>
            </>
          )}
        </g>

        {/* setpoint chip — on the supply run to the building, clear of the pumps */}
        {setpoint != null && (
          <g>
            <rect x={734} y={pumpY - 38} width={128} height={22} rx={6} fill={PANEL} stroke={BD} />
            <text x={798} y={pumpY - 23} textAnchor="middle" fontSize={10} fill={C.mut}>{th ? 'ตั้งค่า' : 'Setpoint'} <tspan fontWeight={800} fill={C.cyan}>{fmt(setpoint, 1)}°C</tspan></text>
          </g>
        )}
      </svg>

      {/* ── bottom band ── real Supply-vs-Return flow comparison when live meters
          are bound; otherwise the 30-day demo trends (Efficiency + Energy). ── */}
      {(liveFlow || liveTemp || livePower) ? (
        <div style={{ borderTop: `1px solid ${BD}`, paddingTop: 8, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {/* 1 · flow (l/m) + temperature (°C) — dual axis */}
          <div style={{ flex: '1 1 340px', minWidth: 300 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 11.5, fontWeight: 700, color: C.txt }}>
                {th ? 'อัตราการไหล + อุณหภูมิ' : 'Flow + Temperature'} <span style={{ fontSize: 9, color: C.green, fontWeight: 700 }}>● {th ? 'ค่าจริง' : 'live'}</span>
              </span>
              <span style={{ fontSize: 10.5, color: C.mut }}>
                <b style={{ color: C.cyan }}>{fmtFlow(supplyFlow)}</b>/<b style={{ color: C.amber }}>{fmtFlow(returnFlow)}</b> {flowUnit} · <b style={{ color: C.green }}>{fmt(chwS, 1)}</b>°C
              </span>
            </div>
            <div style={{ height: 260 }}>
              {renderMetrics({ flow: true, temp: true })}
            </div>
          </div>
          {/* 2 · 3-phase current (A) — live */}
          <div style={{ flex: '1 1 340px', minWidth: 300 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 11.5, fontWeight: 700, color: C.txt }}>
                {th ? 'กระแส 3 เฟส (Current)' : '3-Phase Current'} {livePower && <span style={{ fontSize: 9, color: C.green, fontWeight: 700 }}>● {th ? 'ค่าจริง' : 'live'}</span>}
              </span>
              <span style={{ fontSize: 10.5, color: C.mut }}>
                L1 <b style={{ color: C.cyan }}>{fmt(i1, 1)}</b> · L2 <b style={{ color: C.amber }}>{fmt(i2, 1)}</b> · L3 <b style={{ color: 'var(--purple, #a855f7)' }}>{fmt(i3, 1)}</b> A
              </span>
            </div>
            <div style={{ height: 260 }}>
              {renderMetrics({ current: true })}
            </div>
          </div>
        </div>
      ) : (
        <div style={{ borderTop: `1px solid ${BD}`, paddingTop: 8, display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          <TrendChart
            id="chl-eff"
            title={th ? 'ประสิทธิภาพรวม (kW/RT) · รายวัน 30 วัน' : 'Plant Efficiency (kW/RT) · 30 days'}
            extra={<>{th ? 'ตอนนี้' : 'now'} <b style={{ color: C.green }}>{fmt(kwRt, 2)}</b> · {th ? 'เป้า' : 'target'} <b style={{ color: C.gold }}>≤ {effTarget.toFixed(2)}</b></>}
            series={TREND} color={C.green} target={effTarget}
            yMin={effMin} yMax={effMax} yTicks={[effMax, effTarget, effMin]} fmtY={(v) => v.toFixed(2)}
          />
          <TrendChart
            id="chl-kwh"
            title={th ? 'พลังงานรายวัน (kWh) · 30 วัน' : 'Daily Energy (kWh) · 30 days'}
            extra={<>{th ? 'วันนี้' : 'today'} <b style={{ color: C.amber }}>{fmt(daily, 0)}</b> kWh</>}
            series={ENERGY} color={C.amber}
            yMin={eMin} yMax={eMax} yTicks={[eMax, Math.round((eMin + eMax) / 2), eMin]} fmtY={(v) => String(Math.round(v))}
          />
        </div>
      )}
    </div>
  );
}
